import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  truncateUuid,
} from 'packages/common-frontend/utils';
import { useCreateInvoiceContext } from 'packages/invoices/hooks/useCreateInvoice';
import { LoanDetailsSideDrawer } from 'packages/invoices/ui/create/LoanDetailsSideDrawer';

const InfoCardGridItem = ({
  icon,
  iconColor,
  label,
  value,
}: {
  icon: string;
  iconColor?: string;
  label: string;
  value: string;
}) => {
  return (
    <GridItem>
      <Box
        borderWidth="1px"
        borderColor={colors.gray[100]}
        borderRadius="8px"
        p="16px 24px"
      >
        <Typography variant="detail-muted">{label}</Typography>
        <Flex alignItems="center" gap="16px" height="40px" mt="8px">
          <Icon icon={icon} color={iconColor} />
          <Typography variant="base" fontWeight="semibold">
            {value}
          </Typography>
        </Flex>
      </Box>
    </GridItem>
  );
};

const LoanIdInfoCardGridItem = ({
  loan,
}: {
  loan: ResolvedLoanFragmentGQLType;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <GridItem>
        <Box
          borderWidth="1px"
          borderColor={colors.gray[100]}
          borderRadius="8px"
          p="16px 24px"
        >
          <Typography variant="detail-muted">Loan</Typography>
          <Flex alignItems="center" gap="16px" mt="8px">
            <Icon icon="money-check-dollar" />
            <Button variant="unstyled" onClick={onOpen}>
              <Typography
                fontWeight="semibold"
                variant="base-muted"
                colorScheme="blue"
              >
                {truncateUuid(loan.id)}
                {loan.externalLoanId &&
                  ` (${truncateUuid(loan.externalLoanId)})`}
              </Typography>
            </Button>
          </Flex>
        </Box>
      </GridItem>
      <LoanDetailsSideDrawer loan={loan} isOpen={isOpen} onClose={onClose} />
    </>
  );
};

export const CreateInvoiceLineItemsCards = ({
  invoiceComponentIndex,
}: {
  invoiceComponentIndex: number;
}) => {
  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    selectedEntities: {
      bankAccounts: { sourceBankAccount, maximumAvailableAmountCash },
      creditFacility: { creditFacilityLoans },
    },
  } = useCreateInvoiceContext();

  const invoiceComponent =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex];

  const loan = creditFacilityLoans?.find(
    (creditFacilityLoan) =>
      creditFacilityLoan.id === invoiceComponent.invoiceComponent.finleyLoanId,
  );

  // this component is only relevant if a loan was selected
  if (!invoiceComponent.invoiceComponent.finleyLoanId || !loan) {
    return null;
  }

  return (
    <Grid
      templateColumns="repeat(4, 1fr)"
      gap="24px"
      mb="40px"
      overflowX="auto"
      w="100%"
    >
      <LoanIdInfoCardGridItem loan={loan} />
      <InfoCardGridItem
        icon="calendar"
        label="Origination Date"
        value={commonDate.fullDateStrToHumanDateString(
          loan.dates.originationDate,
        )}
      />
      <InfoCardGridItem
        icon="calendar"
        label="Maturity Date"
        value={commonDate.fullDateStrToHumanDateString(loan.dates.maturityDate)}
      />
      <InfoCardGridItem
        icon="calendar"
        label="Invoice Due Date"
        value={
          invoice.dueDate
            ? commonDate.fullDateStrToHumanDateString(invoice.dueDate)
            : '-'
        }
      />
      {/* Cash borrowing base availability is only relevant if the source bank account is collections */}
      {sourceBankAccount?.accountType === 'collections' &&
        maximumAvailableAmountCash && (
          <InfoCardGridItem
            icon="circle-check"
            iconColor={colors.success}
            label="Maximum Available Amount (Cash)"
            value={dollarNumberWithCommas(maximumAvailableAmountCash) ?? '-'}
          />
        )}
    </Grid>
  );
};
